import Button from 'tio-ui/components/button';
import { getObservableProductRoute } from 'tio-employee/services/observability';
import { getStatus } from 'tio-common/helpers/observability';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import Modal from 'tio-ui/components/modal';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Textarea from 'tio-ui/components/textarea';
import { tracked } from '@glimmer/tracking';
import AlertBanner from 'tio-ui/components/alert-banner';
import Component from '@glimmer/component';
import ExtractionSummary from 'tio-employee/components/observability/extraction-summary';
import ObservabilityDisputeController from 'tio-employee/controllers/authenticated/observability/upload';
import type { ObservableSource } from 'tio-employee/services/observability';
import type IntlService from 'ember-intl/services/intl';
import type ObservabilityService from 'tio-employee/services/observability';
import type ObservableDocumentModel from 'tio-data/models/observable-document';
import type RemoteEventLoggerService from 'tio-employee/services/remote-event-logger';
import { PhCaretLeft, PhFileArrowUp, PhEnvelope } from 'ember-phosphor-icons';
import Chip from 'tio-ui/components/chip';
import type { ChipSignature } from 'tio-ui/components/chip';

interface ObservabilityDisputeRouteSignature {
  Args: {
    controller: ObservabilityDisputeController;
    model: ObservableDocumentModel;
  };
  Element: HTMLDivElement;
}

const getStatusIntent = (status: string): ChipSignature['Args']['intent'] => {
  switch (status) {
    case 'PROCESSING':
      return 'primary';
    case 'INVALID':
    case 'FAILED':
      return 'danger';
    case 'PROCESSED':
      return 'success';
    case 'IN_DISPUTE':
    case 'PARTIALLY_APPLIED':
      return 'warning';
  }
};

const getSubmittedIcon = (via: string) => {
  if (via === 'EMAIL') {
    return PhEnvelope;
  }
  return PhFileArrowUp;
};

export default class ObservabilityDisputeRoute extends Component<ObservabilityDisputeRouteSignature> {
  @service declare intl: IntlService;
  @service declare observability: ObservabilityService;
  @service declare remoteEventLogger: RemoteEventLoggerService;

  @tracked note?: string;
  @tracked loading = false;
  @tracked submitted = false;

  noop = () => {};
  @tracked submissionError?: Error;

  get productSource(): ObservableSource {
    return <ObservableSource>this.args.controller.source;
  }

  get documentCreated() {
    const observableDocument = this.args.model;
    const createdAtDate = new Date(observableDocument.createdAt);
    const date = createdAtDate.toLocaleDateString();
    const time = createdAtDate.toLocaleTimeString();
    const via = this.intl.t(`observability.${observableDocument.via}`);
    return { via, date, time };
  }

  get submitDisabled() {
    if ((!this.note && !this.submissionError) || this.loading || this.submitted) {
      return true;
    }
    return false;
  }

  get document() {
    return this.args.model;
  }

  setNote = (value: string, e?: Event | InputEvent) => {
    if (!e) {
      this.note = value;
      return;
    }

    this.note = (e.target as HTMLTextAreaElement)?.value ?? value;
  };

  submitDispute = async (e: Event) => {
    e.preventDefault();
    const observableDocument = this.args.model;
    this.loading = true;
    try {
      // button is disabled if this.note is unset
      await observableDocument.dispute(this.note!);
      this.remoteEventLogger.logObservabilityEvent({
        eventName: 'PROCESSING_SUCCESS',
        component: 'ObservabilityService',
        document: observableDocument,
      });
      this.submitted = true;
    } catch (e) {
      this.submissionError = e;
    } finally {
      this.loading = false;
    }
  };

  <template>
    <VStack>
      <Header>
        <LinkTo
          @route="authenticated.observability.confirm"
          @model={{@model.id}}
          class="align-bottom font-medium"
        >
          <PhCaretLeft @size="20" class="inline" />
          {{t "common.back"}}
        </LinkTo>
      </Header>
      <Section>
        <:header>
          <h1 class="mb-4">{{t "observability.dispute.header"}}</h1>
          <h2 class="inline">{{@model.observableAsset.filename}}</h2>
          <Chip @intent={{getStatusIntent (getStatus @model)}} class="ml-4">
            {{t "observable_document.status" state=(getStatus @model)}}
          </Chip>
          <p class="tio-row-flush italic text-small font-normal mt-2">
            {{#let (getSubmittedIcon @model.via) as |SubmittedIcon|}}
              <SubmittedIcon @size="24" class="mr-2" />
            {{/let}}
            {{t
              "observability.dispute.document.uploaded"
              via=this.documentCreated.via
              date=this.documentCreated.date
              time=this.documentCreated.time
            }}
          </p>
        </:header>
        <:body>
          <AlertBanner @intent="warning" class="my-4">{{t
              "observability.dispute.review_alert"
            }}</AlertBanner>
          <ExtractionSummary @document={{@model}} class="w-full md:w-2/3" />
          <p class="my-8">{{t "observability.dispute.form.prompt"}}</p>
          <form {{on "submit" this.submitDispute}} class="pb-16">
            <label>
              {{t "observability.dispute.form.note.label"}}
              <Textarea
                @onChange={{this.setNote}}
                @value={{this.note}}
                placeholder={{t "observability.dispute.form.note.placeholder"}}
                class="sm:w-1/2"
              />
            </label>
            <div class="tio-wrap-tight sm:flex-nowrap sm:w-1/2">
              <Button
                type="submit"
                @isDisabled={{this.submitDisabled}}
                @intent="primary"
                @isRunning={{this.loading}}
                class="w-full sm:shrink *:justify-center"
              >
                {{t "common.submit"}}
              </Button>
              <LinkTo
                @route="authenticated.observability.confirm"
                @model={{@model.id}}
                class="tio-btn tio-btn--default tio-btn--outlined tio-btn--md w-full sm:shrink text-center"
              >
                {{t "common.cancel"}}
              </LinkTo>
            </div>
          </form>
        </:body>
      </Section>
    </VStack>
    <Modal
      @isOpen={{this.submitted}}
      @allowCloseButton={{false}}
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{false}}
      @onClose={{this.noop}}
      @size="xl"
    >
      <:body>
        <h2>{{t "observability.dispute.confirmed.header"}}</h2>
        <p class="my-8">{{t "observability.dispute.confirmed.message"}}</p>
        <LinkTo
          {{! @glint-expect-error: this seems like a bug}}
          @route={{getObservableProductRoute @model.source}}
          class="tio-btn tio-btn--primary tio-btn--md w-56 text-center"
        >
          {{t "ok"}}
        </LinkTo>
      </:body>
    </Modal>
  </template>
}
